/**
 * WHAT EACH PLAN IS COMPARED WITH, IN EACH MODULE'S OWN WORDS (owner, 25 Oct 2026).
 *
 * Every card said "+$122,016 vs doing nothing", but "doing nothing" meant something different in each module, and in
 * aged care it was not nothing at all: the person still moves into care, pays the room daily and sells nothing. So
 * each module names its comparison for what it is:
 *
 *   aged care          "paying daily"         — the room paid daily, nothing paid up front, nothing sold
 *                       ("staying put" for a change of home: the current room and deposit kept)
 *   Support at Home    "the support alone"    — the same services, nothing else changed
 *   village, downsizing, granny flat
 *                      "staying put"          — staying in the home, nothing changed
 *                      ("keep renting" for a household that rents)
 *   cashflow           "changing nothing"
 *
 * One helper so every card, rail, Explain this, What could go wrong, Home line and advice document says the same.
 */

export type BenchmarkModule = 'agedCare' | 'supportAtHome' | 'village' | 'downsizer' | 'grannyFlat' | 'cashflow';

export interface BenchmarkHousehold {
    /** The household rents its home (the lifestyle modules' comparison is then "keep renting"). */
    rents?: boolean;
    /** Aged care: a change of home, where the comparison is staying in the current room. */
    facilityMove?: boolean;
}

export interface BenchmarkWords {
    /** The comparison as a noun phrase, lower case: "paying daily". Reads after "vs", "than", "was $X …". */
    word: string;
    /** "vs paying daily". */
    vs: string;
    /** Sentence-case label for a row or column: "Paying daily". */
    label: string;
    /** "was $111,864 paying daily" / "was $2,257 with the support alone" — the word as it follows a figure. */
    after: string;
    /** One plain sentence under step 2's heading: what the comparison is. */
    sentence: string;
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function build(word: string, after: string, sentence: string): BenchmarkWords {
    return { word, vs: `vs ${word}`, label: cap(word), after, sentence };
}

export function benchmarkWords(module: BenchmarkModule, household: BenchmarkHousehold = {}): BenchmarkWords {
    switch (module) {
        case 'agedCare':
            return household.facilityMove
                ? build('staying put', 'staying put', 'Compared with staying in the current room and changing nothing.')
                : build('paying daily', 'paying daily', 'Compared with paying for the room daily and selling nothing.');
        case 'supportAtHome':
            return build('the support alone', 'with the support alone', 'Compared with getting the support and changing nothing else.');
        case 'cashflow':
            return build('changing nothing', 'changing nothing', 'Compared with changing nothing.');
        case 'village':
        case 'downsizer':
        case 'grannyFlat':
        default:
            return household.rents
                ? build('keep renting', 'keeping on renting', 'Compared with keeping on renting where you are and changing nothing.')
                : build('staying put', 'staying put', 'Compared with staying in your home and changing nothing.');
    }
}

/** Aged care's comparison, the common case (no change of home). */
export const AC_BENCH = benchmarkWords('agedCare');
/** Support at Home's comparison. */
export const SAH_BENCH = benchmarkWords('supportAtHome');

/** The household rents the home it lives in (no home of its own on the file). */
export function householdRents(factFind: { client?: { currentResidency?: unknown } | null; partner?: { currentResidency?: unknown } | null; home?: { value?: unknown } | null } | null | undefined): boolean {
    const renting = factFind?.client?.currentResidency === 'renting' || factFind?.partner?.currentResidency === 'renting';
    return renting && !((Number(factFind?.home?.value) || 0) > 0);
}
